/**
 * Raw devices, resolved into the game's action set.
 *
 * The game does not want to know about keys or button indices. It wants a move
 * vector and six verbs, and it wants them to mean the same thing whether they
 * arrived from a keyboard or from a thumbstick. That translation is this
 * module, and it is pure: it is handed a set of held key codes, or a `Gamepad`
 * snapshot, and it hands back `{ move, actions }`.
 *
 * Which keys and which buttons is data, in `bindings.json`, so a remap is an
 * edit to a config file rather than a hunt through the movement code.
 *
 * `move` is a vector, not four booleans, because that is the only shape both
 * devices can produce. A stick is analogue and a key is not, so the keyboard
 * synthesises the vector a stick would have given — including normalising the
 * diagonals, or holding W and D would carry a fighter 1.41x faster than either
 * one alone.
 *
 * +y is *forward*, toward the opponent, and +x is the fighter's right. The
 * standard gamepad mapping reports a stick pushed away from the player as
 * *negative* on its Y axis, because screen space runs downward; that sign flip
 * happens here, once, rather than in every consumer.
 */

export class InputConfigError extends Error {}

/** The verbs the game reasons about. Anything else is a key, not an action. */
export const ACTIONS = Object.freeze([
  'light',
  'heavy',
  'grapple',
  'block',
  'run',
  'taunt',
  'blazin',
]);

/** The four cardinals the move vector is assembled from. */
export const DIRECTIONS = Object.freeze(['forward', 'back', 'left', 'right']);

/** Contribution of each direction to the move vector. */
const AXIS = Object.freeze({
  forward: { x: 0, y: 1 },
  back: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
});

/** Analogue triggers report a value even when a browser leaves `pressed` off. */
const TRIGGER_THRESHOLD = 0.5;

const NOTHING = Object.freeze({ move: Object.freeze({ x: 0, y: 0 }), actions: Object.freeze([]) });

/** Nothing held, nothing pushed. Shared, so a frame with no device allocates nothing. */
export const idleInput = () => NOTHING;

/** Throws on a bindings file that cannot be satisfied, so a typo fails at boot. */
export function validateBindings(bindings) {
  const { keyboard, gamepad } = bindings ?? {};
  if (!keyboard || typeof keyboard !== 'object') {
    throw new InputConfigError('bindings.keyboard must be an object');
  }
  if (!gamepad || typeof gamepad !== 'object') {
    throw new InputConfigError('bindings.gamepad must be an object');
  }

  const expected = [...DIRECTIONS, ...ACTIONS];
  for (const name of expected) {
    if (!Array.isArray(keyboard[name]) || keyboard[name].length === 0) {
      throw new InputConfigError(`keyboard binding "${name}" needs at least one key code`);
    }
    if (!Array.isArray(gamepad[name])) {
      throw new InputConfigError(`gamepad binding "${name}" needs a button index array`);
    }
    for (const index of gamepad[name]) {
      if (!Number.isInteger(index) || index < 0) {
        throw new InputConfigError(`gamepad binding "${name}" has a bad button index ${index}`);
      }
    }
  }

  // An unknown name is almost always a typo for a real one, and silently
  // ignoring it means the key simply never works, with nothing to show why.
  for (const [device, config] of [
    ['keyboard', keyboard],
    ['gamepad', gamepad],
  ]) {
    for (const name of Object.keys(config)) {
      if (expected.includes(name)) continue;
      if (device === 'gamepad' && (name === 'deadzone' || name === 'axes')) continue;
      throw new InputConfigError(`${device} binding "${name}" is not an action or a direction`);
    }
  }

  const { axes, deadzone } = gamepad;
  if (!Number.isInteger(axes?.x) || !Number.isInteger(axes?.y)) {
    throw new InputConfigError('gamepad.axes needs integer x and y indices');
  }
  if (!(deadzone >= 0 && deadzone < 1)) {
    throw new InputConfigError(`gamepad.deadzone ${deadzone} must sit inside 0..1`);
  }

  return bindings;
}

/** Every key code the bindings claim, so the caller knows what to swallow. */
export function boundKeys(bindings) {
  return new Set(Object.values(bindings.keyboard).flat());
}

/**
 * The action state implied by a set of held key codes.
 *
 * `held` is anything with a `.has()` — a Set, in practice.
 */
export function resolveKeyboard(bindings, held) {
  const keyboard = bindings.keyboard;
  const down = (name) => keyboard[name].some((code) => held.has(code));

  let x = 0;
  let y = 0;
  for (const direction of DIRECTIONS) {
    if (!down(direction)) continue;
    x += AXIS[direction].x;
    y += AXIS[direction].y;
  }

  return {
    // Digital keys have to fake the vector a stick would have given, diagonals
    // included — otherwise W+D is 1.41 times as fast as W alone.
    move: clampToUnit({ x, y }),
    actions: ACTIONS.filter(down),
  };
}

/**
 * The action state implied by one `Gamepad` snapshot.
 *
 * The stick and the d-pad both feed the move vector, and the larger push wins
 * rather than the sum: the stick is analogue, and adding a d-pad press to a
 * half-tilted stick would shove it to full tilt.
 */
export function resolveGamepad(bindings, pad) {
  if (!pad) return idleInput();

  const config = bindings.gamepad;
  const axes = pad.axes ?? [];
  const stick = deadzoned(
    { x: axes[config.axes.x] ?? 0, y: -(axes[config.axes.y] ?? 0) },
    config.deadzone
  );

  let x = 0;
  let y = 0;
  for (const direction of DIRECTIONS) {
    if (!anyPressed(pad, config[direction])) continue;
    x += AXIS[direction].x;
    y += AXIS[direction].y;
  }
  const dpad = clampToUnit({ x, y });

  return {
    move: longer(stick, dpad),
    actions: ACTIONS.filter((name) => anyPressed(pad, config[name])),
  };
}

/**
 * Folds several devices into one action state. A verb is held if any device
 * holds it; the move vector is whichever device is pushing hardest, for the
 * same reason the d-pad does not add to the stick.
 */
export function combine(...inputs) {
  let move = { x: 0, y: 0 };
  const actions = new Set();
  for (const input of inputs) {
    if (!input) continue;
    move = longer(move, input.move);
    for (const action of input.actions) actions.add(action);
  }
  return { move, actions: ACTIONS.filter((name) => actions.has(name)) };
}

// --- vector helpers ----------------------------------------------------------

function magnitude(v) {
  return Math.hypot(v.x, v.y);
}

function longer(a, b) {
  return magnitude(b) > magnitude(a) ? b : a;
}

/** Shrinks a vector back to unit length, but never stretches a short one. */
function clampToUnit(v) {
  const length = magnitude(v);
  return length > 1 ? { x: v.x / length, y: v.y / length } : v;
}

/**
 * Radial deadzone, rescaled so the first millimetre of real travel starts at
 * zero rather than at the deadzone edge.
 *
 * Radial, not per-axis: a deadzone applied to x and y separately zeroes one
 * axis whenever the other is near centre, which makes a stick snap to the
 * cardinals near the middle and a diagonal walk impossible to hold.
 */
function deadzoned(v, deadzone) {
  const length = magnitude(v);
  if (length <= deadzone) return { x: 0, y: 0 };
  const scale = Math.min((length - deadzone) / (1 - deadzone), 1) / length;
  return { x: v.x * scale, y: v.y * scale };
}

function anyPressed(pad, indices) {
  return (indices ?? []).some((index) => {
    const button = pad.buttons?.[index];
    if (!button) return false;
    return Boolean(button.pressed) || (button.value ?? 0) > TRIGGER_THRESHOLD;
  });
}
